import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { webhookService } from '../services/webhookService';
import { 
  WebhookResponse, 
  UseWebhookOptions, 
  UseWebhookReturn,
  AnalyticsEventData, 
  ContactEventData, 
  BatchEventData 
} from '../types';

export const useWebhook = (options: UseWebhookOptions = {}): UseWebhookReturn => {
  const queryClient = useQueryClient();
  const [lastResponse, setLastResponse] = useState<WebhookResponse | null>(null);

  const mutation = useMutation({
    mutationFn: async (payload: any): Promise<WebhookResponse> => {
      const { webhookType = 'analytics' } = options;
      
      switch (webhookType) {
        case 'analytics':
          return webhookService.sendAnalyticsEvent(payload as AnalyticsEventData);
        case 'contact':
          return webhookService.sendContactEvent(payload as ContactEventData);
        case 'batch':
          return webhookService.sendBatchEvents(payload as BatchEventData);
        default:
          return webhookService.sendCustomEvent(payload.event, payload.data, webhookType);
      }
    },
    onSuccess: (data) => {
      setLastResponse(data);
      options.onSuccess?.(data);
      
      // 관련 쿼리 무효화
      queryClient.invalidateQueries({ queryKey: ['webhook-events'] });
    },
    onError: (error) => {
      const errorResponse: WebhookResponse = {
        success: false,
        message: error.message,
        error: error.message,
        timestamp: new Date().toISOString(),
      };
      setLastResponse(errorResponse);
      options.onError?.(error);
    },
    retry: options.retryAttempts || 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, options.retryDelay || 30000),
  });

  const sendAnalyticsEvent = useCallback((eventData: AnalyticsEventData) => {
    return mutation.mutateAsync(eventData);
  }, [mutation]);

  const sendContactEvent = useCallback((eventData: ContactEventData) => {
    return mutation.mutateAsync(eventData);
  }, [mutation]);

  const sendBatchEvents = useCallback((events: BatchEventData) => {
    return mutation.mutateAsync(events);
  }, [mutation]);

  const sendCustomEvent = useCallback((eventName: string, eventData: any, webhookType?: 'contact' | 'analytics' | 'batch') => {
    return mutation.mutateAsync({ event: eventName, data: eventData, webhookType });
  }, [mutation]);

  const reset = useCallback(() => {
    mutation.reset();
    setLastResponse(null);
  }, [mutation]);

  return {
    sendAnalyticsEvent,
    sendContactEvent,
    sendBatchEvents,
    sendCustomEvent,
    isLoading: mutation.isPending,
    error: mutation.error,
    lastResponse,
    reset,
  };
}; 